import { createAsyncThunk } from "@reduxjs/toolkit";
import logger from "../utils/logger";

/**
 * Wraps the repeated try/catch/rejectWithValue shell around a backend call.
 *
 * IMPORTANT: the action type string is exactly `typePrefix` — existing
 * prefixes must not change, other code may match on them.
 *
 * @param {string} typePrefix - The exact action type prefix.
 * @param {Function} apiFn - Receives the thunk arg, returns the payload.
 * @param {Object} [options]
 * @param {string} [options.logMessage] - If set, logged (with the error) on failure.
 * @param {string} [options.errorMessage] - rejectWithValue fallback when the error has no message.
 */
export const createApiThunk = (
  typePrefix,
  apiFn,
  { logMessage, errorMessage } = {}
) =>
  createAsyncThunk(typePrefix, async (arg, { rejectWithValue }) => {
    try {
      return await apiFn(arg);
    } catch (error) {
      if (logMessage) {
        logger.error(logMessage, error);
      }
      return rejectWithValue(error.message || errorMessage);
    }
  });

/**
 * Registers the standard pending/fulfilled/rejected flag choreography:
 *   pending   → loadingKey = true,  loadedKey = false, error = null, then onPending(state, action)
 *   fulfilled → loadingKey = false, loadedKey = true,  then onFulfilled(state, action)
 *   rejected  → loadingKey = false, loadedKey = true,  then onRejected(state, action)
 *               (default onRejected: state.error = action.payload)
 */
export const addAsyncCases = (
  builder,
  thunk,
  { loadingKey, loadedKey, onPending, onFulfilled, onRejected }
) => {
  builder
    .addCase(thunk.pending, (state, action) => {
      if (loadingKey) state[loadingKey] = true;
      if (loadedKey) state[loadedKey] = false;
      state.error = null;
      if (onPending) onPending(state, action);
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (loadingKey) state[loadingKey] = false;
      if (loadedKey) state[loadedKey] = true;
      if (onFulfilled) onFulfilled(state, action);
    })
    .addCase(thunk.rejected, (state, action) => {
      if (loadingKey) state[loadingKey] = false;
      if (loadedKey) state[loadedKey] = true;
      if (onRejected) {
        onRejected(state, action);
      } else {
        state.error = action.payload;
      }
    });
};
